import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, UserPlus, Search, CheckCircle2, AlertTriangle, Loader2,
  Download, Upload, FileSpreadsheet, Users,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";

const STATUS_STYLE = {
  criado: { label: "Será criado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  duplicado: { label: "Ignorado (duplicado)", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  invalido: { label: "Inválido", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function AdminImportClients() {
  const [sponsorEmail, setSponsorEmail] = useState("");
  const [sponsor, setSponsor] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null); // {processed,total,counts}
  const [err, setErr] = useState("");

  const resolveSponsor = async () => {
    setResolving(true); setErr(""); setSponsor(null); setPreview(null); setResult(null);
    try {
      const sp = await adminApi.importClientsResolveSponsor(sponsorEmail.trim());
      setSponsor(sp);
    } catch (e) {
      setErr(e.message || "Erro");
      toast.error(e.message || "Erro", { description: "Patrocinador" });
    } finally { setResolving(false); }
  };

  const onFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null); setResult(null); setErr(""); setProgress(null);
  };

  const pollJob = async (jobId) => {
    // Poll até terminar; devolve o job final.
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const job = await adminApi.importClientsStatus(jobId);
      setProgress({ processed: job.processed || 0, total: job.total || 0, counts: job.counts });
      if (job.status === "done") return job;
      if (job.status === "error") throw new Error(job.error || "Falha no processamento");
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1500));
    }
  };

  const runImport = async (dryRun) => {
    if (!sponsor || !file) return;
    setLoading(true); setErr("");
    if (dryRun) { setPreview(null); setResult(null); setProgress(null); }
    try {
      const r = await adminApi.importClients(file, sponsor.Email, dryRun);
      if (dryRun) { setPreview(r); }
      else {
        // r = { job_id, total } — processa em background com polling.
        setProgress({ processed: 0, total: r.total || 0, counts: null });
        const job = await pollJob(r.job_id);
        setResult(job); setPreview(null); setProgress(null);
        toast.success(`${job.counts.criado} cliente(s) criado(s).`, { description: "Importação concluída" });
      }
    } catch (e) {
      setErr(e.message || "Erro");
      toast.error(e.message || "Erro", { description: "Importar Clientes" });
    } finally { setLoading(false); }
  };

  const rowsToShow = (result || preview)?.report || [];
  const counts = (result || preview)?.counts;

  return (
    <div data-testid="admin-import-clients" className="max-w-4xl">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={13} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><UserPlus size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Importar Clientes</h1>
          <p className="text-sm text-[#4B5563]">Carrega vários clientes de um Excel, associados a um patrocinador. Cada cliente fica com a password <span className="font-mono">Passteste01</span> (deve alterá-la depois).</p>
        </div>
      </div>

      {/* Passo 1 — Patrocinador */}
      <div className="bg-white border border-[#E6E1D6] rounded-md p-5 space-y-4 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
          <span className="w-5 h-5 grid place-items-center rounded-full bg-[#0F1829] text-white text-[11px]">1</span>
          Patrocinador da lista
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email" value={sponsorEmail} data-testid="sponsor-email-input"
            onChange={(e) => { setSponsorEmail(e.target.value); setSponsor(null); }}
            onKeyDown={(e) => e.key === "Enter" && resolveSponsor()}
            placeholder="email@patrocinador.pt"
            className="flex-1 px-3 py-2 border border-[#E6E1D6] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1829]/20" />
          <button onClick={resolveSponsor} disabled={!sponsorEmail.trim() || resolving}
            data-testid="resolve-sponsor-btn"
            className="px-4 py-2 bg-[#0F1829] text-white text-sm rounded disabled:opacity-40 hover:bg-black transition inline-flex items-center justify-center gap-2">
            {resolving ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Procurar
          </button>
        </div>
        {sponsor && (
          <div data-testid="sponsor-found" className="text-sm bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3 flex items-center gap-2">
            <Users size={15} className="text-[#0F1829]" />
            <span><strong>#{sponsor.ID_Cliente}</strong> · {sponsor.NomeCompleto} ({sponsor.Email}) — os clientes ficarão associados a esta pessoa.</span>
          </div>
        )}
      </div>

      {/* Passo 2 — Ficheiro */}
      <div className={`bg-white border border-[#E6E1D6] rounded-md p-5 space-y-4 mb-5 ${!sponsor ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
            <span className="w-5 h-5 grid place-items-center rounded-full bg-[#0F1829] text-white text-[11px]">2</span>
            Ficheiro Excel (colunas: nome, telefone, email)
          </div>
          <a href={adminApi.importClientsTemplateUrl()} data-testid="download-template"
            className="text-xs text-[#0F1829] hover:underline inline-flex items-center gap-1">
            <Download size={13} /> Descarregar modelo
          </a>
        </div>
        <label className="flex items-center gap-3 border border-dashed border-[#C9C1AE] rounded p-4 cursor-pointer hover:bg-[#FAF8F2]">
          <FileSpreadsheet size={22} className="text-[#0F1829]" />
          <div className="flex-1">
            <div className="text-sm font-medium">{file ? file.name : "Escolher ficheiro .xlsx"}</div>
            <div className="text-xs text-[#6B7280]">Só são criados emails que ainda não existam no sistema.</div>
          </div>
          <input type="file" accept=".xlsx" onChange={onFile} className="hidden" data-testid="file-input" />
          <span className="px-3 py-1.5 bg-[#0F1829] text-white text-xs rounded">Procurar…</span>
        </label>
        <div className="flex justify-end">
          <button onClick={() => runImport(true)} disabled={!file || loading}
            data-testid="preview-btn"
            className="px-4 py-2 border border-[#0F1829] text-[#0F1829] text-sm rounded disabled:opacity-40 hover:bg-[#F5F2EB] transition inline-flex items-center gap-2">
            {loading && !result ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Pré-visualizar
          </button>
        </div>
      </div>

      {err && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded flex items-start gap-2 mb-5">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{err}</span>
        </div>
      )}

      {/* Progresso da criação em background */}
      {progress && !result && (
        <div className="bg-white border border-[#E6E1D6] rounded-md p-5 mb-5" data-testid="import-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="inline-flex items-center gap-2 font-medium">
              <Loader2 size={15} className="animate-spin" /> A criar clientes…
            </span>
            <span className="text-[#6B7280]">{progress.processed} / {progress.total}</span>
          </div>
          <div className="w-full h-2 bg-[#EFEBE1] rounded-full overflow-hidden">
            <div className="h-full bg-[#0F1829] transition-all"
              style={{ width: `${progress.total ? Math.round((progress.processed / progress.total) * 100) : 0}%` }} />
          </div>
          <p className="text-xs text-[#6B7280] mt-2">Pode aguardar aqui — a operação corre em segundo plano e não é interrompida.</p>
        </div>
      )}

      {/* Resultado / Pré-visualização */}
      {(preview || result) && (
        <div className="bg-white border border-[#E6E1D6] rounded-md p-5 space-y-4" data-testid="import-report">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-2 text-sm">
              <span className="px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="count-criado">
                {result ? "Criados" : "A criar"}: <strong>{counts?.criado ?? 0}</strong>
              </span>
              <span className="px-2.5 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid="count-duplicado">
                Duplicados: <strong>{counts?.duplicado ?? 0}</strong>
              </span>
              <span className="px-2.5 py-1 rounded bg-red-50 text-red-700 border border-red-200" data-testid="count-invalido">
                Inválidos: <strong>{counts?.invalido ?? 0}</strong>
              </span>
            </div>
            {preview && !result && counts?.criado > 0 && (
              <button onClick={() => runImport(false)} disabled={loading}
                data-testid="confirm-import-btn"
                className="px-4 py-2 bg-[#0F1829] text-white text-sm rounded disabled:opacity-40 hover:bg-black transition inline-flex items-center gap-2">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Criar {counts.criado} cliente(s)
              </button>
            )}
            {result && (
              <span className="text-sm text-emerald-700 inline-flex items-center gap-1" data-testid="import-done">
                <CheckCircle2 size={15} /> Importação concluída
              </span>
            )}
          </div>

          <div className="overflow-x-auto border border-[#E6E1D6] rounded">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF8F2] text-[#6B7280] text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 w-12">#</th>
                  <th className="text-left px-3 py-2">Nome</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Telefone</th>
                  <th className="text-left px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {rowsToShow.map((r, i) => {
                  const st = STATUS_STYLE[r.status] || {};
                  return (
                    <tr key={i} className="border-t border-[#EFEBE1]" data-testid={`report-row-${r.row}`}>
                      <td className="px-3 py-2 text-[#6B7280]">{r.row}</td>
                      <td className="px-3 py-2">{r.nome || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.email || "—"}</td>
                      <td className="px-3 py-2">{r.telefone || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded border text-xs ${st.cls || ""}`}>{st.label || r.status}</span>
                        {r.motivo && <span className="block text-[11px] text-[#6B7280] mt-0.5">{r.motivo}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
